
"use client";

import React, { useState } from "react";
import { Row, Col, Input, Typography, Collapse } from "antd";
import Link from "next/link";
import axios from "axios";
import {
    FaInstagram,
    FaFacebookF,
    FaYoutube,
    FaTwitter,
} from "react-icons/fa";
import { BsArrowRight } from "react-icons/bs";
import Image from "next/image";

// Assets
import comverse_ginkgo_logo from "@/assets/images/footer/comverse_ginkgo_logo.svg";

// Styles
import "./FashionFooter.scss";

const { Title, Text } = Typography;

const normalizeFashionLink = (link) => {
    if (!link || link === "#") return link;
    // Remove any leading slash for uniform processing
    const stripped = link.replace(/^\/+/, "");
    // If the link already starts with "fashion/", keep it as-is with a leading slash
    if (stripped.startsWith("fashion/")) return `/${stripped}`;
    // Otherwise prepend /fashion/
    return `/fashion/${stripped}`;
};

const FashionFooter = ({ userAgent, footerNavigationData = [] }) => {
    const [email, setEmail] = useState("");
    const [status, setStatus] = useState(null); // "success" | "already" | "error"
    const [loading, setLoading] = useState(false);

    const handleSubscribe = async () => {
        if (!email || loading) return;
        setLoading(true);
        setStatus(null);
        try {
            await axios.post(
                process.env.NEXT_PUBLIC_BACKEND + "/storefront/newsletter",
                { email, storefront: process.env.NEXT_PUBLIC_STOREFRONT_ID || "23" }
            );
            setStatus("success");
            setEmail("");
        } catch (err) {
            setStatus(err?.response?.status === 400 ? "already" : "error");
        } finally {
            setLoading(false);
        }
    };

    const renderNewsletter = () => (
        <div className="fashion-footer__column fashion-footer__column--newsletter">
            <Title level={4} className="fashion-footer__title">Let&apos;s Keep in Touch</Title>
            <Text className="fashion-footer__subtitle">Subscribe to get special offers</Text>

            <div className="fashion-footer__newsletter">
                <Input
                    placeholder="ENTER EMAIL HERE"
                    className="fashion-footer__input"
                    variant="borderless"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onPressEnter={handleSubscribe}
                    disabled={loading}
                />
                <BsArrowRight
                    className="fashion-footer__newsletter-icon"
                    onClick={handleSubscribe}
                    style={{ cursor: "pointer" }}
                />
            </div>

            {status === "success" && (
                <Text className="fashion-footer__newsletter-msg fashion-footer__newsletter-msg--success">
                    You&apos;re subscribed!
                </Text>
            )}
            {status === "already" && (
                <Text className="fashion-footer__newsletter-msg fashion-footer__newsletter-msg--error">
                    Email already registered.
                </Text>
            )}
            {status === "error" && (
                <Text className="fashion-footer__newsletter-msg fashion-footer__newsletter-msg--error">
                    Something went wrong. Try again.
                </Text>
            )}

            <div className="fashion-footer__socials">
                <Link href="https://instagram.com" target="_blank"><FaInstagram /></Link>
                <Link href="https://facebook.com" target="_blank"><FaFacebookF /></Link>
                <Link href="https://youtube.com" target="_blank"><FaYoutube /></Link>
                <Link href="https://twitter.com" target="_blank"><FaTwitter /></Link>
            </div>
        </div>
    );

    if (userAgent?.isMobile) {
        return (
            <footer className="fashion-footer fashion-footer--mobile">
                <div className="fashion-footer__top-border"></div>
                <div className="fashion-footer__main">
                    <div className="fashion-footer__content-wrapper">
                        {/* Newsletter at top on mobile */}
                        <div className="fashion-footer__mobile-newsletter-wrapper">
                            {renderNewsletter()}
                        </div>

                        {/* Accordion for navigation labels */}
                        <div className="fashion-footer__accordion">
                            <Collapse
                                ghost
                                expandIconPosition="right"
                                accordion
                                className="fashion-footer__collapse"
                                defaultActiveKey={["0"]}
                            >
                                {footerNavigationData?.map((column, idx) => (
                                    <Collapse.Panel
                                        header={<span className="fashion-footer__collapse-header">{column.label}</span>}
                                        key={idx}
                                        className="fashion-footer__collapse-panel"
                                    >
                                        <ul className="fashion-footer__list">
                                            {column.children?.map((child, childIdx) => (
                                                <li key={childIdx}>
                                                    <Link href={normalizeFashionLink(child.link)}>{child.label}</Link>
                                                </li>
                                            ))}
                                        </ul>
                                    </Collapse.Panel>
                                ))}
                                {/* Static panels if needed, e.g., My Account */}
                                <Collapse.Panel
                                    header={<span className="fashion-footer__collapse-header">MY ACCOUNT</span>}
                                    key="my-account"
                                    className="fashion-footer__collapse-panel"
                                >
                                    <ul className="fashion-footer__list">
                                        <li><Link href="/account">LOGIN / REGISTER</Link></li>
                                        {/* <li><Link href="/account/orders">VIEW CART</Link></li>
                                        <li><Link href="/account/wishlist">MY WISHLIST</Link></li>
                                        <li><Link href="/account/track-order">TRACK MY ORDER</Link></li> */}
                                    </ul>
                                </Collapse.Panel>
                            </Collapse>
                        </div>
                    </div>
                </div>

                <div className="fashion-footer__bottom">
                    <div className="fashion-footer__bottom-content">
                        <Text className="fashion-footer__copyright">
                            © Alfamall Fashion 2026. All Rights Reserved
                        </Text>
                        <div className="fashion-footer__tech-partner">
                            <Text>Technical Partner Comverse by</Text>
                            <div className="fashion-footer__partner-logo">
                                <Image src={comverse_ginkgo_logo} alt="Ginkgo Retail" width={100} height={20} />
                            </div>
                        </div>
                    </div>
                </div>
            </footer>
        );
    }

    return (
        <footer className="fashion-footer">
            <div className="fashion-footer__top-border"></div>

            <div className="fashion-footer__main">
                <div className="fashion-footer__content-wrapper">
                    <Row  align="top">
                        {/* Left Side: Two columns with stacked sections */}
                        {footerNavigationData?.[0] && (
                            <Col xs={24} sm={12} md={6} lg={6} xl={6}>
                                <div className="fashion-footer__column-group">
                                    <div className="fashion-footer__column">
                                        <Title level={4} className="fashion-footer__title">{footerNavigationData[0].label}</Title>
                                        <ul className="fashion-footer__list">
                                            {footerNavigationData[0].children?.map((child, childIdx) => (
                                                <li key={childIdx}>
                                                    <Link href={normalizeFashionLink(child.link)}>{child.label}</Link>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    {footerNavigationData?.[2] && (
                                        <div className="fashion-footer__column">
                                            <Title level={4} className="fashion-footer__title">{footerNavigationData[2].label}</Title>
                                            <ul className="fashion-footer__list">
                                                {footerNavigationData[2].children?.map((child, childIdx) => (
                                                    <li key={childIdx}>
                                                        <Link href={normalizeFashionLink(child.link)}>{child.label}</Link>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            </Col>
                        )}

                        {footerNavigationData?.[1] && (
                            <Col xs={24} sm={12} md={6} lg={6} xl={8}>
                                <div className="fashion-footer__column-group">
                                    <div className="fashion-footer__column">
                                        <Title level={4} className="fashion-footer__title">{footerNavigationData[1].label}</Title>
                                        <ul className="fashion-footer__list">
                                            {footerNavigationData[1].children?.map((child, childIdx) => (
                                                <li key={childIdx}>
                                                    <Link href={normalizeFashionLink(child.link)}>{child.label}</Link>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    {footerNavigationData?.[3] && (
                                        <div className="fashion-footer__column">
                                            <Title level={4} className="fashion-footer__title">{footerNavigationData[3].label}</Title>
                                            <ul className="fashion-footer__list">
                                                {footerNavigationData[3].children?.map((child, childIdx) => (
                                                    <li key={childIdx}>
                                                        <Link href={normalizeFashionLink(child.link)}>{child.label}</Link>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            </Col>
                        )}

                        {/* Right Side: Let's Keep in Touch (Static) */}
                        <Col xs={24} sm={24} md={12} lg={12} xl={10} style={{ width: "100%", justifyContent: "flex-end" }} className="fashion-footer__newsletter-col">
                            {renderNewsletter()}
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Bottom Bar */}
            <div className="fashion-footer__bottom">
                <div className="fashion-footer__bottom-content">
                    <Text className="fashion-footer__copyright">
                        © Alfamall Fashion 2026. All Rights Reserved
                    </Text>
                    <div className="fashion-footer__tech-partner">
                        <Text>Technical Partner Comverse by</Text>
                        <div className="fashion-footer__partner-logo">
                            <Image src={comverse_ginkgo_logo} alt="Ginkgo Retail" width={100} height={20} />
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default FashionFooter;
